/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Charyung Kim (crkim@rsupport.com)
 *
 * @description
 *
 * @created at Wed Dec 04 2024
 **/

import { useRef } from "react";
import { fromEvent } from "rxjs";
import { throttle } from "lodash-es";

import { EventHandler } from "@rfice/eventhandler";

import useWhiteboardStore from "../stores/useWhiteboardStore";
import useAutoEvent from "./useAutoEvent";
import { WhiteboardHook } from "./useWhiteboard";

const THROTTLE_TIME = 200;
const DISTANCE_THRESHOLD = 5;

export default function useMouseEvents(userId: string) {
	const setIsMouseDown = useWhiteboardStore((state) => state.setIsMouseDown);
	const whiteboardMode = useWhiteboardStore((state) => state.whiteboardMode);

	const whiteboardRef = useRef<WhiteboardHook>();
	const indicatorRef = useRef<HTMLElement | null>();
	const isMouseDown = useRef<boolean>(false);
	const prevIsMouseDown = useRef<boolean>(false);

	/* Initialize */
	const initialize = (whiteboard: WhiteboardHook) => {
		whiteboardRef.current = whiteboard;
		indicatorRef.current = document.getElementById("player-indicator");
	};

	/* Mouse Events */
	useAutoEvent(
		[
			{
				event: fromEvent<MouseEvent>(document, "mousedown"),
				handler: (e: MouseEvent) => handleMouseEvents(e)
			},
			{
				event: fromEvent<MouseEvent>(document, "mousemove"),
				handler: (e: MouseEvent) => handleMouseEvents(e)
			},
			{
				event: fromEvent<MouseEvent>(document, "mouseup"),
				handler: (e: MouseEvent) => handleMouseEvents(e)
			},
			{
				event: fromEvent<MouseEvent>(document, "mouseout"),
				handler: (e: MouseEvent) => handleMouseEvents(e)
			}
		],
		[whiteboardMode]
	);

	let positions: Vector2[] = [];

	const fireEvents = throttle(() => {
		if (isMouseDown.current) {
			EventHandler.fire("$MouseEvents::Draw$", positions);
			positions = [];
		}
	}, THROTTLE_TIME);

	const handleMouseEvents = (e: MouseEvent) => {
		if (e.target != whiteboardRef.current?.getCanvasRef()) return;

		const position = { x: e.offsetX, y: e.offsetY };
		const indicatorPosition = whiteboardMode === "drawing" ? position : { x: e.clientX, y: e.clientY };

		switch (e.type) {
			case "mousedown": {
				isMouseDown.current = true;
				whiteboardRef.current?.drawingStart(userId, position);
				EventHandler.fire("$MouseEvents::DrawStart$", position);
				break;
			}

			case "mousemove": {
				if (isMouseDown.current) {
					whiteboardRef.current?.drawingTo(userId, position);

					// 필요한 position만 저장
					sortPositions(position);
					fireEvents();
				}
				break;
			}

			case "mouseup":
			case "mouseout": {
				if (isMouseDown.current) {
					isMouseDown.current = false;
					whiteboardRef.current?.drawingEnd(userId, position);
					EventHandler.fire("$MouseEvents::DrawEnd$", position);
					positions = [];
				}
				break;
			}
		}

		e.stopImmediatePropagation();
		updatePlayerIndicator(indicatorPosition, e.type);
		setIsMouseDown(isMouseDown.current);

		prevIsMouseDown.current = isMouseDown.current;
	};

	const updatePlayerIndicator = (position: Vector2, type: string) => {
		if (indicatorRef.current) {
			indicatorRef.current.style.left = position.x + "px";
			indicatorRef.current.style.top = position.y + "px";
			indicatorRef.current.style.visibility = type === "mouseout" ? "hidden" : "visible";
		}
	};

	/* Utils */
	const sortPositions = (position: Vector2) => {
		if (positions.length == 0) {
			positions.push(position);
		} else {
			const prevPosition = positions[positions.length - 1];
			const distance = getDistance(prevPosition, position);
			if (distance > DISTANCE_THRESHOLD) positions.push(position);
		}
	};

	const getDistance = (prev: Vector2, cur: Vector2) => {
		const dx = prev.x - cur.x;
		const dy = prev.y - cur.y;
		return Math.sqrt(dx * dx + dy * dy);
	};

	return {
		initialize
	};
}
